/**
 * Theme toggle — dark/light com persistência em localStorage e respeito
 * inicial a `prefers-color-scheme` quando não houver preferência salva.
 *
 * Tokens canônicos: pacote @tmf/design-tokens (repo TMF/design-tokens, brand
 * priceshow), importado em apps/admin/src/index.css via
 * `@tmf/design-tokens/priceshow/tailwind-v4.css` (tokens --color-*).
 * NÃO usar as cópias locais em design-system/ como referência do admin.
 *
 * O atributo `data-theme` no <html> é o switch — todos os tokens dependem
 * dele via `[data-theme="dark"]` / `[data-theme="light"]`.
 */

import { useEffect, useState, useCallback } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "priceshow:theme";

function readStored(): Theme | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "dark" || v === "light" ? v : null;
}

function systemPreference(): Theme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.style.colorScheme = theme;
}

/**
 * Aplica o tema inicial (lendo localStorage ou prefers-color-scheme).
 * Chamar antes de React montar para evitar flash of unstyled content.
 */
export function initTheme(): Theme {
  const initial = readStored() ?? systemPreference();
  applyTheme(initial);
  return initial;
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(() => readStored() ?? systemPreference());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return { theme, setTheme, toggle };
}
